import {getEnv} from '../../common/env/env';
import {getCommonBadge} from '../../common/getCommonBadge';
import {getSubtitleList} from '../../common/getSubtitleList';
import {ItemSeriesDto} from '../../common/shared/api/gateway/ItemSeriesDto.interface';
import {ItemSingleDto} from '../../common/shared/api/gateway/ItemSingleDto.interface';
import {SlideTypeVo} from '../../common/shared/api/nicolas/vo/enums/SlideTypeVo.enum';
import {getDiscountRateText} from '../../common/shared/utils/getDiscountRateText';
import {getFormattedNumberWithSuffix} from '../../common/shared/utils/getFormattedNumberWithSuffix';
import {getKageUrl} from '../../common/shared/utils/getKageUrl';
import {
  CleanViewItem,
  ItemType,
  Maybe,
  MetaInfoType,
  User,
} from '../generated/types';
import {createAgeGrade, getAgeGradeVo} from './AgeGrade';

export function createCleanViewItem(
  userInfo: Maybe<User>,
  param:
    | {itemSeriesDto: ItemSeriesDto; metaInfoType?: Maybe<MetaInfoType>}
    | {itemSingleDto: ItemSingleDto}
): CleanViewItem {
  if ('itemSeriesDto' in param) {
    return withItemSeriesDto(userInfo, param.itemSeriesDto, param.metaInfoType);
  } else {
    return withItemSingleDto(userInfo, param.itemSingleDto);
  }
}

export function withItemSeriesDto(
  userInfo: Maybe<User>,
  {
    authors,
    age_grade,
    badge,
    business_model,
    is_waitfree,
    waitfree_period_by_minute,
    series_id,
    thumbnail,
    title,
    on_issue,
    operator_property,
    service_property,
    asset_property,
  }: ItemSeriesDto,
  metaInfoType?: Maybe<MetaInfoType>
): CleanViewItem {
  const {badgeList, ageGradeBadge, statusBadge} = getCommonBadge({
    itemType: ItemType.PosterView,
    badge,
    age_grade,
    is_waitfree,
    waitfree_period_by_minute: waitfree_period_by_minute || null,
    business_model,
    self_censorship: operator_property?.self_censorship,
  });

  const cardThumbnail = asset_property?.clean_img || thumbnail;

  return {
    id: `CleanView-itemSereis-${series_id}-${metaInfoType}`,
    type: ItemType.CleanView,
    ageGrade: createAgeGrade(age_grade),
    selfCensorship: operator_property?.self_censorship,
    title,
    badgeList,
    ageGradeBadge,
    statusBadge,
    showPlayerIcon: false,
    thumbnail: cardThumbnail.startsWith('http')
      ? cardThumbnail
      : getKageUrl({
          kageUrlPrefix: getEnv().kageUrlPrefix,
          kageId: cardThumbnail,
          resolution: 'th3',
        }),
    subtitleList: getSubtitleList({
      itemType: ItemType.PosterView,
      metaInfoType,
      servicePropertyDto: service_property,
      onIssueVo: on_issue,
      authors,
    }),
    scheme: `kakaopage://open/content?series_id=${series_id}`,
    rank: service_property?.rank ?? undefined,
    discountRate: operator_property?.cash_info?.discount_rate,
    discountRateText: getDiscountRateText({
      discountRate: operator_property?.cash_info?.discount_rate,
      setDiscountRate: operator_property?.cash_info?.set_discount_rate,
    }),
  };
}

export function withItemSingleDto(
  userInfo: Maybe<User>,
  {
    age_grade,
    badge,
    product_id,
    thumbnail,
    title,
    operator_property,
    service_property,
    series_property,
    slide_type,
    asset_property,
  }: ItemSingleDto
): CleanViewItem {
  const {
    is_waitfree = false,
    waitfree_period_by_minute,
    business_model,
  } = series_property || {};
  const ageGradeVo = getAgeGradeVo({
    singleAgeGrade: age_grade,
    seriesAgeGrade: series_property?.age_grade,
  });
  const {badgeList, ageGradeBadge, statusBadge} = getCommonBadge({
    itemType: ItemType.PosterView,
    badge,
    age_grade: ageGradeVo,
    self_censorship: operator_property?.self_censorship,
    is_waitfree,
    waitfree_period_by_minute: waitfree_period_by_minute || null,
    business_model,
  });
  const cardThumbnail = asset_property?.clean_img || thumbnail;
  return {
    id: `CleanView-itemSingle-${product_id}`,
    type: ItemType.CleanView,
    showPlayerIcon:
      slide_type === SlideTypeVo.TalkHumor ||
      slide_type === SlideTypeVo.VideoOnly,
    ageGrade: createAgeGrade(ageGradeVo),
    selfCensorship: operator_property?.self_censorship,
    title,
    thumbnail: cardThumbnail.startsWith('http')
      ? cardThumbnail
      : getKageUrl({
          kageUrlPrefix: getEnv().kageUrlPrefix,
          kageId: cardThumbnail,
          resolution: 'th3',
        }),
    badgeList,
    ageGradeBadge,
    statusBadge,
    subtitleList: service_property?.view_count
      ? [getFormattedNumberWithSuffix(service_property?.view_count)]
      : [],
    scheme: `kakaopage://open/viewer?product_id=${product_id}&series_id=${series_property?.series_id}`,
    discountRate: operator_property?.cash_info?.discount_rate,
    discountRateText: getDiscountRateText({
      discountRate: operator_property?.cash_info?.discount_rate,
      setDiscountRate: operator_property?.cash_info?.set_discount_rate,
    }),
  };
}
